import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import type { ImageFormat } from '@liks/backend-svc-api/esm/models/index';
import type { ImageAspectRatio } from '../../../types/gallery';
import type { LiksChatComposerProps } from './LiksChatComposer.types';

type UseLiksChatComposerActionsOptions = {
  disabled: boolean;
  isSendBlocked: boolean;
  hasUploadingAttachments: boolean;
  hasReadyAttachments: boolean;
  isAttachmentsLocked: boolean;
  isKnowledgeLocked: boolean;
  isWebSearchLocked: boolean;
  isImageGenerationLocked: boolean;
  isSpeechTranscribing: boolean;
  isSpeechRequesting: boolean;
  message: string;
  setMessage: Dispatch<SetStateAction<string>>;
  webSearchEnabled: boolean;
  imageGenerationEnabled: boolean;
  imageFormat: ImageFormat;
  imageAspectRatio: ImageAspectRatio;
  setIsLocallySendLocked: (locked: boolean) => void;
  setIsAttachmentsMenuOpen: (open: boolean) => void;
  setIsKnowledgeMenuOpen: (open: boolean) => void;
  setIsImageMenuOpen: (open: boolean) => void;
  setWebSearchEnabled: Dispatch<SetStateAction<boolean>>;
  setImageGenerationEnabled: Dispatch<SetStateAction<boolean>>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  onAddFiles: LiksChatComposerProps['onAddFiles'];
  onRemoveStagedAttachment: LiksChatComposerProps['onRemoveStagedAttachment'];
  onSend: LiksChatComposerProps['onSend'];
  onSendFailed: LiksChatComposerProps['onSendFailed'];
  onKnowledgeBaseToggle: LiksChatComposerProps['onKnowledgeBaseToggle'];
  toggleSpeechRecording: () => void;
};

export function useLiksChatComposerActions({
  disabled,
  isSendBlocked,
  hasUploadingAttachments,
  hasReadyAttachments,
  isAttachmentsLocked,
  isKnowledgeLocked,
  isWebSearchLocked,
  isImageGenerationLocked,
  isSpeechTranscribing,
  isSpeechRequesting,
  message,
  setMessage,
  webSearchEnabled,
  imageGenerationEnabled,
  imageFormat,
  imageAspectRatio,
  setIsLocallySendLocked,
  setIsAttachmentsMenuOpen,
  setIsKnowledgeMenuOpen,
  setIsImageMenuOpen,
  setWebSearchEnabled,
  setImageGenerationEnabled,
  textareaRef,
  onAddFiles,
  onRemoveStagedAttachment,
  onSend,
  onSendFailed,
  onKnowledgeBaseToggle,
  toggleSpeechRecording,
}: UseLiksChatComposerActionsOptions) {
  const messageRef = useRef(message);
  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  const handleClear = useCallback(() => {
    setMessage('');
    textareaRef.current?.focus();
  }, [setMessage, textareaRef]);

  const handleAddFiles = useCallback(
    (fileList: FileList | null) => {
      if (isAttachmentsLocked || !fileList || fileList.length === 0) {
        return;
      }
      onAddFiles?.(Array.from(fileList));
    },
    [isAttachmentsLocked, onAddFiles],
  );

  const handleRemoveStagedAttachment = useCallback(
    (attachmentId: string) => {
      onRemoveStagedAttachment?.(attachmentId);
    },
    [onRemoveStagedAttachment],
  );

  const handleSend = useCallback(() => {
    const trimmedMessage = message.trim();
    if (trimmedMessage === '' && !hasReadyAttachments) {
      return;
    }
    if (disabled || isSendBlocked || hasUploadingAttachments) {
      return;
    }

    setIsLocallySendLocked(true);
    messageRef.current = '';
    setMessage('');
    setIsAttachmentsMenuOpen(false);
    textareaRef.current?.focus();

    void Promise.resolve(
      onSend?.(trimmedMessage, {
        webSearchEnabled,
        imageGenerationEnabled,
        ...(imageGenerationEnabled ? { imageFormat, imageAspectRatio } : {}),
      }),
    )
      .catch((error: unknown) => {
        const draftStillEmpty = messageRef.current.trim() === '';
        if (draftStillEmpty) {
          setMessage(trimmedMessage);
        }
        onSendFailed?.({
          error,
          failedText: trimmedMessage,
          restoredToComposer: draftStillEmpty,
        });
      })
      .finally(() => {
        setIsLocallySendLocked(false);
      });
  }, [
    disabled,
    hasReadyAttachments,
    hasUploadingAttachments,
    imageAspectRatio,
    imageFormat,
    imageGenerationEnabled,
    isSendBlocked,
    message,
    onSend,
    onSendFailed,
    setIsAttachmentsMenuOpen,
    setIsLocallySendLocked,
    setMessage,
    textareaRef,
    webSearchEnabled,
  ]);

  const handleKnowledgeBaseToggle = useCallback(
    (knowledgeBaseId: string) => {
      if (isKnowledgeLocked) {
        return;
      }
      onKnowledgeBaseToggle?.(knowledgeBaseId);
    },
    [isKnowledgeLocked, onKnowledgeBaseToggle],
  );

  const handleWebSearchToggle = useCallback(() => {
    if (isWebSearchLocked) {
      return;
    }
    setIsAttachmentsMenuOpen(false);
    setIsKnowledgeMenuOpen(false);
    setIsImageMenuOpen(false);
    setWebSearchEnabled((current) => !current);
  }, [
    isWebSearchLocked,
    setIsAttachmentsMenuOpen,
    setIsImageMenuOpen,
    setIsKnowledgeMenuOpen,
    setWebSearchEnabled,
  ]);

  const handleImageGenerationToggle = useCallback(() => {
    if (isImageGenerationLocked) {
      return;
    }
    setIsAttachmentsMenuOpen(false);
    setIsKnowledgeMenuOpen(false);
    setImageGenerationEnabled((current) => !current);
  }, [
    isImageGenerationLocked,
    setImageGenerationEnabled,
    setIsAttachmentsMenuOpen,
    setIsKnowledgeMenuOpen,
  ]);

  const closeMenusAndToggleSpeech = useCallback(() => {
    if (isSpeechTranscribing || isSpeechRequesting) {
      return;
    }
    setIsAttachmentsMenuOpen(false);
    setIsKnowledgeMenuOpen(false);
    setIsImageMenuOpen(false);
    toggleSpeechRecording();
  }, [
    isSpeechRequesting,
    isSpeechTranscribing,
    setIsAttachmentsMenuOpen,
    setIsImageMenuOpen,
    setIsKnowledgeMenuOpen,
    toggleSpeechRecording,
  ]);

  return {
    handleClear,
    handleAddFiles,
    handleRemoveStagedAttachment,
    handleSend,
    handleKnowledgeBaseToggle,
    handleWebSearchToggle,
    handleImageGenerationToggle,
    closeMenusAndToggleSpeech,
  };
}
